import { t } from "i18next"
import fp from "lodash/fp"

import type { TFunction } from "i18next"

import type { PlotView } from "types/dashboard.types"

export const translateBoolean = (value: boolean) => {
  return value ? t("generic.YES") : t("generic.NO")
}

/** Entity names are optional in the API, so one without a name shows as "Unnamed". */
export const nameOrUnnamed = (name: string | null | undefined, t: TFunction): string =>
  name || t("generic.UNNAMED")

export const getTranslationKey = (text: string) => {
  return text
    .replace(/[^A-Za-z]+/g, " ")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase()
}

const getPlotUnit = (view: PlotView) => {
  switch (view.type) {
    case "xy":
      switch (view.properties.axes.y.type) {
        case "number":
          return view.properties.axes.y.suffix
        default:
          return ""
      }
    case "gauge":
      return view.properties.suffix
    case "single-stat":
      return view.properties.suffix
    default:
      return ""
  }
}

export const getPlotName = (
  view: PlotView,
  withUnit: boolean,
): { nameKey: string; unit: string; tooltip: string[] | undefined } => {
  const dividerIndex = view.name.lastIndexOf("[")
  const keyFromName = dividerIndex > -1 ? view.name.slice(0, dividerIndex) : view.name

  const key = view.nameKey.length ? view.nameKey : keyFromName

  const nameKey = getTranslationKey(key)
  const unit = withUnit ? `[${getPlotUnit(view)}]` : ""
  const tooltip = view.helpTooltip

  return { nameKey, unit, tooltip }
}

export const addEndPeriodIfMissing = (text: string) => {
  return text.trimEnd().slice(-1) === "." ? text : text + "."
}

type ToSentenceCase = (text: string) => string
export const toSentenceCase: ToSentenceCase = fp.capitalize

const upperFirst = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

// Per-segment tower-health fields carry a dynamic segment name (the key of
// settings.features.tower_health.segments), so they can't have static i18n keys.
// This builds a label like "Lower segment's longitudinal bending" / "Lower segment's torsion"
// from the pattern tower.<segment>.bending.<longitudinal|lateral|magnitude>[.agg] and
// tower.<segment>.torsion[.agg].
const getSegmentTelemetryLabel = (name: string, t: TFunction): string | undefined => {
  const parts = name.split(".")
  if (parts[0] !== "tower" || parts.length < 3) return undefined
  const segment = parts[1]
  // Skip the whole-tower ("global") and per-device ("point") namespaces.
  if (segment === "global" || segment === "point") return undefined

  const rest = parts.slice(2)
  const tail = rest[rest.length - 1] === "agg" ? rest.slice(0, -1) : rest
  const [metric, component] = tail
  const opts = { segment: upperFirst(segment) }

  if (metric === "torsion" && tail.length === 1) {
    return t("telemetries.segment_torsion", opts)
  }
  if (metric === "bending" && tail.length === 2) {
    if (["longitudinal", "lateral", "magnitude"].includes(component)) {
      return t(`telemetries.segment_bending_${component}`, opts)
    }
  }
  return undefined
}

export const getTelemetryTranslation = (name: string, t: TFunction) => {
  const segmentLabel = getSegmentTelemetryLabel(name, t)
  if (segmentLabel) return segmentLabel

  const formattedFieldName = name.replaceAll(".", "_")
  const defaultValue = fp.capitalize(name.replaceAll(".", " "))
  return t(`telemetries.${formattedFieldName}`, { defaultValue })
}
